import { useCallback, useEffect, useRef, useState } from 'react'
import { domyslnyWybor, type WspolnyWybor } from './wybor'
import { wczytajWybor, zapiszWybor, type Pobierz } from './preferencje'

const OPOZNIENIE_ZAPISU_MS = 400

const takiSam = (a: WspolnyWybor, b: WspolnyWybor) => JSON.stringify(a) === JSON.stringify(b)

/**
 * Wspólny wybór nagłówka zapamiętany dla zalogowanej osoby.
 * - Odczyt przy otwarciu ekranu i — gdy `czytajPrzyPowrocie` — po powrocie do karty (inny moduł mógł zmienić
 *   wybór). Ten sam wybór z serwera nie podmienia obiektu, więc ekrany nie ładują danych ponownie.
 * - Zmiany użytkownika to „łatka” na wartość z serwera: zmiana zrobiona przed pierwszym odczytem nie kasuje
 *   zapamiętanych pól, a zapis czeka na ten odczyt.
 * - Zapis 400 ms po ostatniej zmianie; niewysłana zmiana idzie od razu, gdy karta się chowa albo ekran znika.
 *   Odczyt czeka na zapis w toku.
 * - Pusty `adres` (np. przed zalogowaniem) = ani odczytu, ani zapisu; odczyt rusza, gdy adres się pojawi.
 */
export function useWspolnyWybor({ pobierz, adres, czytajPrzyPowrocie = true }: {
  pobierz: Pobierz
  adres: string
  czytajPrzyPowrocie?: boolean
}): { wybor: WspolnyWybor; zaladowany: boolean; ustaw: (zmiana: Partial<WspolnyWybor>) => void } {
  const [wybor, setWybor] = useState<WspolnyWybor>(domyslnyWybor)
  const [zaladowany, setZaladowany] = useState(false)
  const pobierzRef = useRef(pobierz)
  pobierzRef.current = pobierz
  const biezacy = useRef<WspolnyWybor>(wybor)
  const latka = useRef<Partial<WspolnyWybor> | null>(null)
  const poOdczycie = useRef(false)
  const zapisWToku = useRef<Promise<unknown> | null>(null)
  const zegar = useRef<ReturnType<typeof setTimeout> | null>(null)

  const zapiszTeraz = useCallback(() => {
    if (zegar.current) { clearTimeout(zegar.current); zegar.current = null }
    // Przed pierwszym odczytem nie zapisujemy: łatka trafi na wartość z serwera.
    if (!adres || !poOdczycie.current || !latka.current) return
    latka.current = null
    const zapis = zapiszWybor(pobierzRef.current, adres, biezacy.current)
    zapisWToku.current = zapis
    void zapis.finally(() => { if (zapisWToku.current === zapis) zapisWToku.current = null })
  }, [adres])

  const zaplanujZapis = useCallback(() => {
    if (zegar.current) clearTimeout(zegar.current)
    zegar.current = setTimeout(zapiszTeraz, OPOZNIENIE_ZAPISU_MS)
  }, [zapiszTeraz])

  const czytaj = useCallback(async () => {
    if (!adres) return
    if (zapisWToku.current) await zapisWToku.current.catch(() => {})
    const w = await wczytajWybor(pobierzRef.current, adres)
    if (w) {
      const nowy = latka.current ? { ...w, ...latka.current } : w
      if (!takiSam(nowy, biezacy.current)) { biezacy.current = nowy; setWybor(nowy) }
    }
    const pierwszy = !poOdczycie.current
    poOdczycie.current = true
    setZaladowany(true)
    if (pierwszy && latka.current) zaplanujZapis()
  }, [adres, zaplanujZapis])

  useEffect(() => { void czytaj() }, [czytaj])

  useEffect(() => {
    const naZmianeWidocznosci = () => {
      if (document.visibilityState === 'hidden') zapiszTeraz()
      else if (czytajPrzyPowrocie) void czytaj()
    }
    document.addEventListener('visibilitychange', naZmianeWidocznosci)
    return () => document.removeEventListener('visibilitychange', naZmianeWidocznosci)
  }, [czytaj, czytajPrzyPowrocie, zapiszTeraz])

  useEffect(() => () => zapiszTeraz(), [zapiszTeraz])

  const ustaw = useCallback((zmiana: Partial<WspolnyWybor>) => {
    const nowy = { ...biezacy.current, ...zmiana }
    biezacy.current = nowy
    latka.current = { ...(latka.current ?? {}), ...zmiana }
    setWybor(nowy)
    if (poOdczycie.current) zaplanujZapis()
  }, [zaplanujZapis])

  return { wybor, zaladowany, ustaw }
}
